import React, { useState, useEffect } from "react";
import "./teamManagement.css";
import API_Root from "../../Constants/API_Root";
import { useDispatch } from "react-redux";
import { toggleToast } from "../../Actions/ToggleToast";
import { authFetch } from "../../Authentication/authService";

const KbManage = ({
  filesForApproval = [],
  lastSynced,
  handleSyncFiles,
  handleCheckSyncStatus,
  filenames = [],
  handleDeleteFile,
  selectedTeamId,
  teamStorageUsage,
}) => {
  console.log(filenames, "filename");
  const dispatch = useDispatch();

  const [folders, setFolders] = useState([]);
  const [newFolder, setNewFolder] = useState("");

  const [draggingFiles, setDraggingFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [targetFolder, setTargetFolder] = useState("");
  const [activeDropFolder, setActiveDropFolder] = useState(null);

  const [activeFolder, setActiveFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [loadingFolderFiles, setLoadingFolderFiles] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  // =========================
  // FETCH FOLDERS
  // =========================
  const fetchFolders = async () => {
    if (!selectedTeamId) return;

    try {
      const res = await authFetch(`${API_Root}files/get-folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_id: selectedTeamId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      setFolders(data?.folders || []);
    } catch (err) {
      dispatch(
        toggleToast({ show: true, type: "error", message: err.message })
      );
    }
  };

  useEffect(() => {
    fetchFolders();
  }, [selectedTeamId]);

  // =========================
  // FETCH FILES UNDER FOLDER
  // =========================
  const fetchFilesByFolder = async (folder) => {
    setLoadingFolderFiles(true);

    try {
      const res = await authFetch(`${API_Root}files/files-under-team`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          folder_name: folder,
          team_id: selectedTeamId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      setFolderFiles(data?.files || []);
    } catch (err) {
      dispatch(
        toggleToast({ show: true, type: "error", message: err.message })
      );
    } finally {
      setLoadingFolderFiles(false);
    }
  };

  const toggleFileSelection = (file) => {
    setSelectedFiles((prev) => {
      const exists = prev.find((f) => f.filename === file.filename);

      if (exists) {
        return prev.filter((f) => f.filename !== file.filename);
      }

      return [...prev, file];
    });
  };

  // =========================
  // TOGGLE FOLDER
  // =========================
  const handleFolderClick = (folder) => {
    setActiveFolder(folder);
    setSelectedFiles([]);
    fetchFilesByFolder(folder);
  };

  const handleBack = () => {
    setActiveFolder(null);
    setFolderFiles([]);
    setSelectedFiles([]);
  };

  // =========================
  // APPROVE FILE
  // =========================
  const handleApproveFile = async (fileId) => {
    try {
      const res = await authFetch(API_Root + "files/approve-file", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: fileId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "File approved",
        })
      );

      // Refresh files after approval
      handleCheckSyncStatus?.();
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    }
  };

  const handleMoveSelectedFiles = async () => {
    if (!selectedFiles.length || !targetFolder) return;

    try {
      await Promise.all(
        selectedFiles.map(async (file) => {
          const res = await authFetch(`${API_Root}files/move-file`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              filename: file.filename,
              destination: targetFolder,
            }),
          });

          const data = await res.json();

          if (!res.ok) {
            throw new Error(data?.message);
          }
        })
      );

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: `${selectedFiles.length} file(s) moved`,
        })
      );

      setSelectedFiles([]);
      setTargetFolder("");

      fetchFolders();

      if (activeFolder) {
        fetchFilesByFolder(activeFolder);
      }
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    }
  };

  // =========================
  // CREATE FOLDER
  // =========================
  const handleCreateFolder = async () => {
    if (!newFolder || !selectedTeamId) return;

    try {
      const res = await authFetch(`${API_Root}files/create-folder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          folder_name: newFolder,
          team_id: selectedTeamId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      dispatch(
        toggleToast({ show: true, type: "success", message: "Folder created" })
      );

      setNewFolder("");
      fetchFolders();
    } catch (err) {
      dispatch(
        toggleToast({ show: true, type: "error", message: err.message })
      );
    }
  };

  // =========================
  // MOVE FILE
  // =========================
  const handleMoveMultipleFiles = async (folder) => {
    if (!draggingFiles.length) return;

    try {
      await Promise.all(
        draggingFiles.map(async (file) => {
          const res = await authFetch(`${API_Root}files/move-file`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              filename: file.filename,
              destination: folder,
            }),
          });

          const data = await res.json();

          if (!res.ok) {
            throw new Error(data?.message);
          }
        })
      );

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: `${draggingFiles.length} file(s) moved successfully`,
        })
      );

      setDraggingFiles([]);
      setSelectedFiles([]);

      fetchFolders();

      if (activeFolder) {
        fetchFilesByFolder(activeFolder);
      }
    } catch (err) {
      dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: err.message,
        })
      );
    }
  };

  // =========================
  // DRAG EVENTS
  // =========================
  const handleDragStart = (file) => {
    const isSelected = selectedFiles.some((f) => f.filename === file.filename);

    if (isSelected && selectedFiles.length > 0) {
      setDraggingFiles(selectedFiles);
    } else {
      setDraggingFiles([file]);
    }
  };

  const handleDrop = (folder) => {
    handleMoveMultipleFiles(folder);
    setActiveDropFolder(null);
  };

  // =========================
  // FILES TO SHOW
  // =========================
  const filesToShow = activeFolder ? folderFiles : filenames;

  const filteredFiles = filesToShow?.filter((file) =>
    file?.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedFiles = filteredFiles.reduce((acc, file) => {
    const category = file?.metadata?.category || "Uncategorized";

    if (!acc[category]) {
      acc[category] = [];
    }

    acc[category].push(file);

    return acc;
  }, {});

  const maxStorage = 5 * 1024 * 1024 * 1024;

  const usedStorageGB = (teamStorageUsage / (1024 * 1024 * 1024)).toFixed(2);

  const usedPercentage = Math.min((teamStorageUsage / maxStorage) * 100, 100);

  return (
    <div className="tm-wrapper">
      {/* STORAGE */}
      <div className="tm-card storage-card">
        <div className="storage-header">
          <h4>Storage Usage</h4>

          <span>{usedPercentage.toFixed(0)}% Used</span>
        </div>

        <div className="storage-value">
          {usedStorageGB} GB
          <span> / 5 GB</span>
        </div>

        <div className="tm-progress">
          <div
            className="tm-progress-fill"
            style={{
              width: `${usedPercentage}%`,
            }}
          />
        </div>
      </div>

      {/* FILES PENDING APPROVAL */}
      {filesForApproval?.length > 0 && (
        <div className="tm-card">
          <h4>Files Pending Approval</h4>

          <div className="tm-files-list">
            {filesForApproval.map((file, index) => (
              <div
                key={file.id || index}
                className="tm-file-card pending-approval-card"
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {file.filename || file.file_name}
                  </span>

                  <small style={{ color: "#6b7280" }}>Awaiting approval</small>
                </div>

                <button
                  className="approve-btn"
                  onClick={() => handleApproveFile(file.id)}
                >
                  Approve
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FILE SYSTEM */}
      <div className="tm-card">
        <div className="kb-header">
          <h4>Files & Folders</h4>

          <div className="kb-actions">
            <input
              className="kb-search"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="create-folder">
              <input
                placeholder="Folder name"
                value={newFolder}
                onChange={(e) => setNewFolder(e.target.value)}
              />

              <button onClick={handleCreateFolder}>Create Folder</button>
            </div>
          </div>
        </div>

        {/* Folder Section */}

        <div className="section-title">Folders</div>

        <div className="folder-grid">
          {folders.length === 0 ? (
            <p className="empty">No folders found</p>
          ) : (
            folders.map((folder) => (
              <div
                key={folder}
                className={`folder-card
          ${activeFolder === folder ? "active-folder-card" : ""}
          ${activeDropFolder === folder ? "drop-target" : ""}
        `}
                onClick={() => handleFolderClick(folder)}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={() => setActiveDropFolder(folder)}
                onDragLeave={() => setActiveDropFolder(null)}
                onDrop={() => handleDrop(folder)}
              >
                <div className="folder-icon">📁</div>

                <div className="folder-name">{folder}</div>

                <small>Click to view files</small>
              </div>
            ))
          )}
        </div>

        {/* Breadcrumb */}

        {activeFolder && (
          <div className="breadcrumb-bar">
            <button className="tm-back-btn" onClick={handleBack}>
              ← Back
            </button>

            <span>Home / {activeFolder}</span>
          </div>
        )}

        {/* Bulk Actions */}

        {selectedFiles.length > 0 && (
          <div className="bulk-action-bar">
            <span>{selectedFiles.length} file(s) selected</span>

            <select
              value={targetFolder}
              onChange={(e) => setTargetFolder(e.target.value)}
            >
              <option value="">Select Folder</option>

              {folders.map((folder) => (
                <option key={folder} value={folder}>
                  {folder}
                </option>
              ))}
            </select>

            <button disabled={!targetFolder} onClick={handleMoveSelectedFiles}>
              Move Files
            </button>
          </div>
        )}

        {/* Files */}

        <div className="section-title">Files</div>

        {loadingFolderFiles ? (
          <div className="empty">Loading files...</div>
        ) : Object.keys(groupedFiles).length === 0 ? (
          <div className="empty">No files found</div>
        ) : (
          Object.entries(groupedFiles).map(([category, files]) => (
            <div key={category} className="category-group">
              <div className="category-header">
                <h4>{category}</h4>

                <span>
                  {files.length} File
                  {files.length > 1 ? "s" : ""}
                </span>
              </div>

              <div className="files-table">
                {files.map((file) => (
                  <div
                    key={file.filename}
                    className={`file-row ${
                      selectedFiles.some((f) => f.filename === file.filename)
                        ? "selected-row"
                        : ""
                    }`}
                    draggable
                    onDragStart={() => handleDragStart(file)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedFiles.some(
                        (f) => f.filename === file.filename
                      )}
                      onChange={() => toggleFileSelection(file)}
                    />

                    <div className="file-info">
                      <div className="file-name">
                        📄 {file.filename?.split("/")?.pop()}
                      </div>

                      <div className="file-tags">
                        <span className="category-tag">
                          {file.metadata?.category}
                        </span>

                        <span className="subcategory-tag">
                          {file.metadata?.subcategory}
                        </span>
                      </div>
                    </div>

                    <button
                      className="danger"
                      onClick={() => handleDeleteFile(file.filename)}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      {/* META TAGS */}
      {/* <div className="tm-card">
        <h4>Meta Tags</h4>

        <div className="tm-tags">
          <span className="tm-tag">ESAP</span>
          <span className="tm-tag">Compliance</span>
          <span className="tm-tag">Finance</span>
        </div>
      </div> */}
    </div>
  );
};

export default KbManage;
